<template>
  <Suspense>
    <template #default>
      <!-- When blocked by entitlement (upgrade modal opened) or redirected
           to the canvas screen, avoid rendering any object. -->
      <div
        v-if="redirectedToUpgrade || redirectedToCanvas"
        class="custom-object-loading"
      />
      <template v-else>
        <!-- TODO: like Agent, DefaultOject (e.g Event) etc. the data fetching should be done in
             the route and passed to the actual object component. -->
        <ObjectComponent
          :database-id="tableId"
          :workspace-id="workspaceId"
          :id="rowId"
        />
      </template>
    </template>
    <template #fallback>
      <ObjectLoader />
    </template>
  </Suspense>
</template>

<script lang="ts">
import { defineComponent, toRefs } from "vue";

import type { Task } from "@ctrl/controller-rpc";

import {
  canvasScreenLocation,
  RAW_OBJECT_QUERY,
} from "@/components/canvas/useCanvases";
import ObjectComponent from "@/components/graph/Object.vue";
import * as inject from "@/components/inject";
import ObjectLoader from "@/components/ObjectLoader.vue";
import { workspaceBlockedByEntitlement } from "@/components/workspaces";

export default defineComponent({
  components: {
    ObjectComponent,
    ObjectLoader,
  },
  props: {
    workspaceId: { type: String, required: true },
    tableId: { type: String, required: true },
    rowId: { type: String, required: false },
  },
  emits: {
    openTask: (_task: Task) => true,
  },
  async setup(props) {
    const controller = inject.controller();
    const { workspaceId, tableId, rowId } = toRefs(props);
    const profile = inject.profile();
    const billing = inject.billing();
    const router = inject.router();
    const logger = inject.logger("CustomObject");

    logger.info("CustomObject", {
      workspaceId: workspaceId.value,
      tableId: tableId.value,
      rowId: rowId.value,
    });

    // Update the injected current workspace ID
    inject.currentWorkspaceId(workspaceId);

    // Canvas rows open as their consumable canvas screen — the custom
    // object page is an implementation detail of the canvas. It stays
    // reachable with the `raw` query flag (the canvas top bar's "Open"
    // action).
    const canvasLocation =
      rowId.value &&
      router.currentRoute.value.query[RAW_OBJECT_QUERY] === undefined
        ? canvasScreenLocation(
            profile.value,
            workspaceId.value,
            tableId.value,
            rowId.value
          )
        : null;
    const redirectedToCanvas = canvasLocation !== null;
    if (canvasLocation) await router.replace(canvasLocation);

    // Workspaces of custom organizations require the "workspaces"
    // entitlement; open the upgrade modal instead of the object when blocked.
    const redirectedToUpgrade =
      !redirectedToCanvas &&
      (await workspaceBlockedByEntitlement({
        controller,
        profile: profile.value,
        billing,
        workspaceId: workspaceId.value,
        logger,
      }));

    return {
      redirectedToUpgrade,
      redirectedToCanvas,
    };
  },
});
</script>
